import GoogleLogo from 'assets/icons/google-logo.svg'
import { Button } from 'common/components/Button/Button'
import { useEffect, useState } from 'react'
import { ApiPathsSocial, socialGoogleLogin } from '../social.service'
import { useGoogleSignIn } from '../useGoogleSignIn'
import { useSocialLoginFlow } from '../useSocialLoginFlow'

interface IGoogleSignInButtonProps {
  redirectTo?: string
  /** Disables the button, e.g. while the email/password form or the Apple button is mid-flow. */
  disabled?: boolean
  /** Reports whether this button is currently mid-flow, so sibling login controls can disable themselves. */
  onLoadingChange?: (isLoading: boolean) => void
}

export const GoogleSignInButton = ({ redirectTo, disabled, onLoadingChange }: IGoogleSignInButtonProps) => {
  const { isReady, loadError, requestAccessToken } = useGoogleSignIn()
  const [isRequestingToken, setIsRequestingToken] = useState(false)

  const { trigger: triggerSocialLogin, isMutating } = useSocialLoginFlow(ApiPathsSocial.google, socialGoogleLogin, {
    redirectTo,
    genericErrorMessage: 'Google sign-in failed. Please try again.',
  })

  const isLoading = isRequestingToken || isMutating

  useEffect(() => {
    onLoadingChange?.(isLoading)
  }, [isLoading, onLoadingChange])

  const handleClick = async () => {
    try {
      setIsRequestingToken(true)
      const accessToken = await requestAccessToken()
      await triggerSocialLogin({ accessToken }, { throwOnError: false })
    } catch {
      // User cancelled the Google popup — no error toast, matches spec §9.
    } finally {
      setIsRequestingToken(false)
    }
  }

  return (
    <Button
      type="button"
      variant="white"
      className="w-full gap-2 border-round-lg"
      size="large"
      disabled={disabled || !isReady || loadError}
      isLoading={isLoading}
      onClick={handleClick}
      data-cy="social-login-google"
    >
      <GoogleLogo style={{ width: '20px', height: '20px' }} /> Continue with Google
    </Button>
  )
}
